import React, { FC, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { EligibleRoute, SecureRoute } from '../../../packages/iam/src/components';
import { CardsProvider } from '../../../packages/card/src/contexts/Card';
import { CardActionsProvider } from '../../../packages/card/src/contexts/CardActions';
import { CardDetailsWrapper } from '../../../packages/card/src/contexts/CardDetails';
import { useCustomerType } from '../../../packages/userProfile/src/contexts/UserInfo/hooks';
import { useIsCardAdmin } from '../../../packages/transaction/src/contexts/Parameters/hooks';
import { CustomerType } from '../../../packages/userProfile/src/domain/UserInfo/entities/UserInfo';
import { CardActionsRouter } from './CardActionsRouter';

const CardsListPage = lazy(
  () => import(/* webpackChunkName: "CardsListPageComponent" */ '../../../packages/card/src/pages/CardsListPage'),
);

const IssueCardPage = lazy(
  () => import(/* webpackChunkName: "IssueCardPageComponent" */ '../../../packages/card/src/pages/IssueCardPage'),
);

const ConsentCardPage = lazy(
  () => import(/* webpackChunkName: "IssueCardPageComponent" */ '../../../packages/card/src/pages/ConsentCardPage'),
);

const RequestCardPage = lazy(
  () => import(/* webpackChunkName: "RequestCardPageComponent" */ '../../../packages/card/src/pages/RequestCardPage'),
);

const IssueCorporateCardPage = lazy(
  () =>
    import(/* webpackChunkName: "IssueCardPageComponent" */ '../../../packages/card/src/pages/IssueCorporateCardPage'),
);

const IndividualCardsRouter: FC<GenericObject> = ({ brandConfig }) => {
  const customerType = useCustomerType();
  const isCorporateUser = customerType === CustomerType.CORPORATE;
  return (
    <CardsProvider>
      <Routes>
        <Route
          path='/cards'
          element={
            <SecureRoute requiredEntitlements={UserAppTransaction.PrepaidCardList}>
              <EligibleRoute>
                <CardDetailsWrapper>
                  <CardActionsProvider>
                    <CardsListPage pageConfig={brandConfig.cardsListPage} />
                  </CardActionsProvider>
                </CardDetailsWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/card/:cardId/*'
          element={
            <CardActionsRouter
              requiredEntitlements={UserAppTransaction.PrepaidCardList}
              brandConfig={brandConfig}
              isCorporate={isCorporateUser}
            />
          }
        />
        <Route
          path='/cards/issue-card'
          element={
            <SecureRoute
              logic={'AND'}
              requiredEntitlements={[UserAppTransaction.PrepaidCardList, UserAppTransaction.PrepaidCardIssue]}
            >
              <EligibleRoute>
                <CardDetailsWrapper>
                  <IssueCardPage pageConfig={brandConfig.issueCardPage} />
                </CardDetailsWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/cards/consent-card'
          element={
            <SecureRoute logic={'AND'} requiredEntitlements={[UserAppTransaction.CardConsent]}>
              <EligibleRoute>
                <CardDetailsWrapper>
                  <ConsentCardPage pageConfig={brandConfig.consentCardPage} />
                </CardDetailsWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/cards/request-card'
          element={
            <SecureRoute
              logic='AND'
              requiredEntitlements={[UserAppTransaction.PrepaidCardList, UserAppTransaction.RequestCard]}
            >
              <EligibleRoute>
                <CardDetailsWrapper>
                  <RequestCardPage pageConfig={brandConfig.requestCardPage} />
                </CardDetailsWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
      </Routes>
    </CardsProvider>
  );
};

const CorporateCardsRouter: FC<GenericObject> = ({ brandConfig }) => {
  const isCardAdmin = useIsCardAdmin();
  return (
    <CardsProvider isCorporate>
      <Routes>
        <Route
          path='/cards'
          element={
            <SecureRoute requiredEntitlements={UserAppTransaction.CorporateCardList} isCardAdmin={isCardAdmin}>
              <EligibleRoute>
                <CardDetailsWrapper>
                  <CardActionsProvider>
                    <CardsListPage pageConfig={brandConfig.corporateCardsListPage} isCorporateAdminPage />
                  </CardActionsProvider>
                </CardDetailsWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/card/:cardId/*'
          element={
            <CardActionsRouter
              requiredEntitlements={[UserAppTransaction.CorporateCardList, UserAppTransaction.PrepaidCardDetails]}
              isCorporate={true}
              logic={'AND'}
              brandConfig={brandConfig}
              isCorporateAdminPage
            />
          }
        />
        <Route
          path='/cards/issue-card'
          element={
            <SecureRoute
              logic='AND'
              requiredEntitlements={[UserAppTransaction.CorporateCardList, UserAppTransaction.IssueCorporateCard]}
              isCardAdmin={isCardAdmin}
            >
              <EligibleRoute>
                <CardDetailsWrapper>
                  <IssueCorporateCardPage pageConfig={brandConfig.issueCorporateCardPage} />
                </CardDetailsWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
      </Routes>
    </CardsProvider>
  );
};

export { IndividualCardsRouter, CorporateCardsRouter };
